import React from 'react';
import MatchEvent from '../../../interfaces/matchEvent';
import isGoalEvent from '../../../features/match/helpers/isGoalEvent';
import EventTextEmpty from '../EventTextEmpty';
import EventTextGoal from '../EventTextGoal';
import EventTextOwnGoal from '../EventTextOwnGoal';
import { FormattedMessage } from 'react-intl';
import EventTextSubstitution from '../EventTextSubstitution';
import EventTextPenalty from '../EventTextPenalty';
import EventTextPenaltyMiss from '../EventTextPenaltyMiss';
import { MatchEventType, matchStatusEvents } from '../../../enums/matchEventType';
import EventTextPenaltyGoal from '../EventTextPenaltyGoal';

/**
 *
 * @param matchEvent
 * @return JSX.Element|null
 */
const getEventText = (matchEvent: MatchEvent): JSX.Element | null | undefined => {
  const team = matchEvent?.team?.name;
  const player = matchEvent?.person?.name;

  let message: any;

  const showEmptyMessage =
    !player &&
    !matchStatusEvents.includes(matchEvent.matchEventTypeId) &&
    !isGoalEvent(matchEvent) &&
    matchEvent.matchEventTypeId !== MatchEventType.PENALTY &&
    matchEvent.matchEventTypeId !== MatchEventType.PENALTY_MISS &&
    matchEvent.matchEventTypeId !== MatchEventType.COMMENT &&
    matchEvent.matchEventTypeId !== MatchEventType.VAR &&
    matchEvent.matchEventTypeId !== MatchEventType.PLAYER_OFF;
  // handle empty text for matchEventTypeIds that are not goal events.

  if (showEmptyMessage) {
    return <EventTextEmpty matchEvent={matchEvent} />;
  }

  switch (matchEvent.matchEventTypeId) {
    case MatchEventType.GOAL: {
      message = <EventTextGoal matchEvent={matchEvent} />;
      break;
    }

    case MatchEventType.OWN_GOAL: {
      message = <EventTextOwnGoal matchEvent={matchEvent} />;
      break;
    }

    case MatchEventType.DISALLOWED_GOAL: {
      const id = player ? 'disallowedGoalWithPlayer' : 'disallowedGoal';

      message = (
        <FormattedMessage
          id={`eventText.${id}`}
          values={{
            player,
          }}
        />
      );
      break;
    }

    case MatchEventType.PENALTY_MISS:
    case MatchEventType.PENALTIES_MISS: {
      message = <EventTextPenaltyMiss matchEvent={matchEvent} />;
      break;
    }

    case MatchEventType.PENALTIES_GOAL:
      message = <EventTextPenaltyGoal matchEvent={matchEvent} />;
      break;

    case MatchEventType.PENALTY:
    case MatchEventType.PENALTIES_PENALTY: {
      message = <EventTextPenalty matchEvent={matchEvent} />;
      break;
    }

    case MatchEventType.YELLOW_CARD: {
      message = (
        <FormattedMessage
          id="eventText.yellowCard"
          values={{
            player,
            team,
          }}
        />
      );
      break;
    }

    case MatchEventType.SECOND_YELLOW_CARD: {
      message = (
        <FormattedMessage
          id="eventText.secondYellowCard"
          values={{
            player,
            team,
          }}
        />
      );
      break;
    }

    case MatchEventType.RED_CARD: {
      message = (
        <FormattedMessage
          id="eventText.redCard"
          values={{
            player,
            team,
          }}
        />
      );
      break;
    }

    case MatchEventType.CORNER: {
      message = (
        <FormattedMessage
          id="eventText.corner"
          values={{
            player,
            team,
          }}
        />
      );
      break;
    }

    case MatchEventType.PLAYER_OFF: {
      message = <EventTextSubstitution matchEvent={matchEvent} />;
      break;
    }

    case MatchEventType.FREE_KICK: {
      message = <FormattedMessage id="eventText.freeKick" values={{ team, player }} />;
      break;
    }

    case MatchEventType.MATCH_KICKS_OFF: {
      message = <FormattedMessage id="states.startFirstHalf" />;
      break;
    }

    case MatchEventType.HALF_TIME: {
      message = <FormattedMessage id="states.pause" />;
      break;
    }

    case MatchEventType.SECOND_HALF: {
      message = <FormattedMessage id="states.startSecondHalf" />;
      break;
    }

    case MatchEventType.FIRST_HALF_EXTRA_TIME: {
      message = <FormattedMessage id="states.startFirstHalfExtraTime" />;
      break;
    }

    case MatchEventType.HALF_TIME_EXTRA_TIME: {
      message = <FormattedMessage id="states.halfTimeExtraTime" />;
      break;
    }

    case MatchEventType.SECOND_HALF_EXTRA_TIME: {
      message = <FormattedMessage id="states.startSecondHalfExtraTime" />;
      break;
    }

    case MatchEventType.MATCH_ENDS: {
      message = <FormattedMessage id="states.ended" />;
      break;
    }

    case MatchEventType.OFFSIDE: {
      message = <FormattedMessage id="eventText.offside" values={{ team, player }} />;
      break;
    }

    case MatchEventType.VAR: {
      message = <FormattedMessage id="eventText.VAR" />;
      break;
    }

    case MatchEventType.LINEUP_READY: {
      message = <FormattedMessage id="eventText.lineupReady" />;
      break;
    }
  }

  return message;
};

export default getEventText;
